import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import { UserInfoResponse } from "@/contracts/auth-api.types";
import { authService } from "@/services/authService";
import { clearAccessToken, refreshAccessTokenSingleFlight } from "@/services/apiClient";

export interface AuthContextType {
  user: UserInfoResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUserInfo = useCallback(async () => {
    try {
      const userInfo = await authService.getUserInfo();
      setUser(userInfo);
    } catch {
      setUser(null);
      clearAccessToken();
    }
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      setIsLoading(true);
      try {
        await authService.login(username, password);
        await fetchUserInfo();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchUserInfo],
  );

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch {
      // Ignora erros ao deslogar
    } finally {
      setUser(null);
      clearAccessToken();
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  // Restauração silenciosa de sessão ao carregar a aplicação
  useEffect(() => {
    let isMounted = true;

    async function restoreSession() {
      try {
        const token = await refreshAccessTokenSingleFlight();
        if (token && isMounted) {
          const userInfo = await authService.getUserInfo();
          if (isMounted) {
            setUser(userInfo);
          }
        }
      } catch {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void restoreSession();

    // Event listener para expiração de sessão disparado pelo apiClient
    const handleSessionExpired = () => {
      setUser(null);
      clearAccessToken();
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      isMounted = false;
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
